import { useState, useEffect } from 'react';
import AppLayout from '../layout/AppLayout';
import { Form } from 'react-bootstrap';
import Cookies from 'js-cookie';
import axios from 'axios';
import '../../App.css';
import './colorConfig.css'; // New CSS
import Loader from '../loader/loader';
import { Spinner } from 'react-bootstrap';
import regenerateToken from '../regenerateToken';
import { FaPalette, FaUserMd, FaVial, FaFileMedicalAlt, FaMagic, FaSave, FaCheckCircle } from 'react-icons/fa';
import { useAlert } from 'react-alert';
import { buildRequestConfig } from '../../utils/apiHelper';
import { isAdmin as checkAdmin } from '../../utils/authUtils';

/**
 * Reusable Row Component for a single color input
 */
const ColorRow = ({ label, value, onChange }) => {
  return (
    <div className='cc-color-row'>
      <div className='cc-label-group'>
        <span className='cc-label-text'>{label}</span>
        <span className='cc-label-code'>{value}</span>
      </div>
      <div className='cc-color-circle' style={{ backgroundColor: value }}>
        <input
          type='color'
          className='cc-input-hidden'
          value={value}
          onChange={onChange}
          title='Choose color'
        />
      </div>
    </div>
  );
};

const ColorConfig = () => {
  const alert = useAlert();
  const [patientHeaderHeading, setPatientHeaderHeading] = useState('#757575');
  const [patientHeaderValue, setPatientHeaderValue] = useState('#5D2482');
  const [profileHeading, setProfileHeading] = useState('#5D2482');
  const [profileBackground, setProfileBackground] = useState('#F7EAFF');
  const [saveOption, setSaveOption] = useState(false);
  const [colorConfig, setColorConfig] = useState({
    colorObj: {
      colored: {
        normal: '#80c8b3',
        finalCritial: '#dd9690',
        oneFromNormal: '#F4B400',
        twoFromNormal: '#C47D63',
        threeFromNormal: '#C26564',
      },
      greyscaled: {
        normal: '#D2D2D2',
        finalCritial: '#111111',
        oneFromNormal: '#969696',
        twoFromNormal: '#7B7B7B',
        threeFromNormal: '#444444',
      },
    },
  });
  const [parameterColor, setParameterColor] = useState({
    normal: '#E4FEF2',
    finalCritial: '#FFEAE8',
    oneFromNormal: '#FCF%E2',
    twoFromNormal: '#C47D63',
    threeFromNormal: '#C26564',
  });

  const [loading, setLoading] = useState(false);
  const [confirmationLoading, setConfirmationLoading] = useState(false);

  const getColorConfiguration = async () => {
    // ── ADMIN PATH: use admin token + inject clientId if a client is selected
    if (checkAdmin()) {
      return await axios(buildRequestConfig({
        method: 'GET',
        url: 'https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/config/color',
      }));
    }
    // ── CLIENT PATH (unchanged) ──────────────────────────────────────────
    const idToken = Cookies.get('idToken');
    const existingColorConfigBody = {
      headers: {
        authorization: idToken,
      },
      method: 'GET',
      url: 'https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/config/color',
      validateStatus: function (status) {
        return status >= 200 && status <= 401;
      },
    };
    const existingColorConfigurationResponse = await axios(
      existingColorConfigBody
    );
    return existingColorConfigurationResponse;
  };

  const updatingExistingColorConfiguration = response => {
    const { colorObj } = response.data.data;
    // Updating the default values into the state objects
    setColorConfig({
      colorObj: {
        colored: colorObj.colored,
        greyscaled: colorObj.greyscaled,
      },
    });
    setPatientHeaderHeading(colorObj.reportColors.patientHeader.heading);
    setPatientHeaderValue(colorObj.reportColors.patientHeader.value);
    setProfileHeading(colorObj.reportColors.profile.heading);
    setProfileBackground(colorObj.reportColors.profile.background);
    setParameterColor(colorObj.reportColors.parameter);
    setLoading(false);
  };

  useEffect(() => {
    (async function RetrieveExistingColorConfiguration() {
      setLoading(true);
      const existingColorConfigurationResponse = await getColorConfiguration();
      const statusCode = existingColorConfigurationResponse.status;

      if (statusCode === 401) {
        await regenerateToken();
        const existingColorConfigurationResponse = getColorConfiguration();
        if (existingColorConfigurationResponse.data.isSuccess) {
          console.log('updated Color Configuration @ second trail');
          updatingExistingColorConfiguration(
            existingColorConfigurationResponse
          );
        }
      } else {
        if (existingColorConfigurationResponse.data.isSuccess) {
          console.log('updated Color Configuration @ first trail');
          updatingExistingColorConfiguration(
            existingColorConfigurationResponse
          );
        }
      }
    })();
  }, []);

  const handlingUpdate = (key1, key2, value) => {
    setColorConfig(prevConfig => ({
      ...prevConfig,
      colorObj: {
        ...prevConfig.colorObj,
        [key1]: {
          ...prevConfig.colorObj[key1],
          [key2]: value,
        },
      },
    }));
  };


  const handlingConfirmation = async () => {
    // This function acts as "Publish" - effectively the same API call 
    // but semantically confirms "Release" to the user.
    // In this specific implementation, since we only have one POST endpoint,
    // both Save and Publish technically update the config.
    // However, the UI distinction allows us to gate "Done" vs "Reviewing".

    // Re-using the same object construction logic
    const finalColorConfig = {
      colorObj: {
        ...colorConfig.colorObj,
        reportColors: {
          patientHeader: {
            heading: patientHeaderHeading,
            value: patientHeaderValue,
          },
          profile: {
            heading: profileHeading,
            background: profileBackground,
          },
          parameter: parameterColor,
        },
      },
    };

    // ── ADMIN PATH: use admin token + inject clientId if a client is selected
    if (checkAdmin()) {
      try {
        setConfirmationLoading(true);
        await axios(buildRequestConfig({
          method: 'POST',
          url: 'https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/config/color',
          data: finalColorConfig.colorObj,
        }));
        setConfirmationLoading(false);
        window.location.reload();
      } catch (e) {
        console.log('Error in API', e.message);
        alert.error('Failed to publish changes. Please try again.');
        setConfirmationLoading(false);
      }
      return;
    }
    // ── CLIENT PATH (unchanged) ──────────────────────────────────────────
    const idToken = Cookies.get('idToken');
    const ColorConfigurationBody = {
      headers: {
        authorization: idToken,
      },
      method: 'POST',
      url: 'https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/config/color',
      data: JSON.stringify(finalColorConfig.colorObj),
    };
    try {
      setConfirmationLoading(true);
      await axios(ColorConfigurationBody);
      setConfirmationLoading(false);
      window.location.reload();
    } catch (e) {
      console.log('Error in API', e.message);
      alert.error('Failed to publish changes. Please try again.');
      setConfirmationLoading(false);
    }
  };

  return (
    <AppLayout>
      {loading ? (
        <Loader />
      ) : (
        <div className='cc-container'>
          {/* Header with Actions */}
          <div className='cc-header'>
            <div className='cc-title-group'>
              <h1 className='cc-title'>Color Configuration</h1>
              <p className='cc-subtitle'>
                Customize the visual appearance of your reports and UI elements.
              </p>
            </div>

            <div className='cc-actions-top'>
              <div className={`cc-status-pill ${saveOption ? 'unsaved' : ''}`}>
                <div className="cc-status-indicator"></div>
                <span>{saveOption ? 'Unsaved Changes' : 'All Changes Saved'}</span>
              </div>

              {/* Save button removed as per request for auto-save behavior */}

              <button
                className='cc-btn cc-btn-primary'
                onClick={() => handlingConfirmation()}
                disabled={!saveOption}
              >
                {confirmationLoading ? (
                  <Spinner animation="border" role="status" size="sm"></Spinner>
                ) : (
                  <>
                    <FaCheckCircle /> Publish
                  </>
                )}
              </button>
            </div>
          </div>

          <div className='cc-bento-grid'>
            {/* 1. Coloured Report Colors - Large Card */}
            <div className='cc-card cc-card-large'>
              <div className='cc-card-header'>
                <div className='cc-icon-box' style={{ background: '#eff6ff', color: '#2563eb' }}><FaPalette /></div>
                <h3 className='cc-card-title'>Coloured Report Colors</h3>
              </div>
              <p className='cc-card-desc'>
                How result values (normal, abnormal, borderline, high) are highlighted in the report when viewed in colour.
              </p>
              <div className='cc-card-body'>
                <ColorRow
                  label="Normal"
                  value={colorConfig.colorObj.colored.normal}
                  onChange={e => { setSaveOption(true); handlingUpdate('colored', 'normal', e.target.value); }}
                />
                <ColorRow
                  label="Abnormal"
                  value={colorConfig.colorObj.colored.finalCritial}
                  onChange={e => { setSaveOption(true); handlingUpdate('colored', 'finalCritial', e.target.value); }}
                />
                <ColorRow
                  label="Borderline"
                  value={colorConfig.colorObj.colored.oneFromNormal}
                  onChange={e => { setSaveOption(true); handlingUpdate('colored', 'oneFromNormal', e.target.value); }}
                />
                <ColorRow
                  label="Very High"
                  value={colorConfig.colorObj.colored.twoFromNormal}
                  onChange={e => { setSaveOption(true); handlingUpdate('colored', 'twoFromNormal', e.target.value); }}
                />
                <ColorRow
                  label="Very Very High"
                  value={colorConfig.colorObj.colored.threeFromNormal}
                  onChange={e => { setSaveOption(true); handlingUpdate('colored', 'threeFromNormal', e.target.value); }}
                />
              </div>
            </div>

            {/* 2. Greyscaled Report Colors - Large Card */}
            <div className='cc-card cc-card-large'>
              <div className='cc-card-header'>
                <div className='cc-icon-box' style={{ background: '#f1f5f9', color: '#64748b' }}><FaMagic /></div>
                <h3 className='cc-card-title'>Greyscaled Report Colors</h3>
              </div>
              <p className='cc-card-desc'>
                Same ranges for greyscale or print-friendly reports.
              </p>
              <div className='cc-card-body'>
                <ColorRow
                  label="Normal"
                  value={colorConfig.colorObj.greyscaled.normal}
                  onChange={e => { setSaveOption(true); handlingUpdate('greyscaled', 'normal', e.target.value); }}
                />
                <ColorRow
                  label="Abnormal"
                  value={colorConfig.colorObj.greyscaled.finalCritial}
                  onChange={e => { setSaveOption(true); handlingUpdate('greyscaled', 'finalCritial', e.target.value); }}
                />
                <ColorRow
                  label="Borderline"
                  value={colorConfig.colorObj.greyscaled.oneFromNormal}
                  onChange={e => { setSaveOption(true); handlingUpdate('greyscaled', 'oneFromNormal', e.target.value); }}
                />
                <ColorRow
                  label="Very High"
                  value={colorConfig.colorObj.greyscaled.twoFromNormal}
                  onChange={e => { setSaveOption(true); handlingUpdate('greyscaled', 'twoFromNormal', e.target.value); }}
                />
                <ColorRow
                  label="Very Very High"
                  value={colorConfig.colorObj.greyscaled.threeFromNormal}
                  onChange={e => { setSaveOption(true); handlingUpdate('greyscaled', 'threeFromNormal', e.target.value); }}
                />
              </div>
            </div>

            {/* 3. Parameter Colors - Large Card */}
            <div className='cc-card cc-card-large'>
              <div className='cc-card-header'>
                <div className='cc-icon-box' style={{ background: '#ecfdf5', color: '#10b981' }}><FaVial /></div>
                <h3 className='cc-card-title'>Parameter Colors</h3>
              </div>
              <p className='cc-card-desc'>
                Background colour of each parameter row (test + value) in the report.
              </p>
              <div className='cc-card-body'>
                <ColorRow
                  label="Normal"
                  value={parameterColor.normal}
                  onChange={e => { setSaveOption(true); setParameterColor({ ...parameterColor, normal: e.target.value }); }}
                />
                <ColorRow
                  label="Abnormal"
                  value={parameterColor.finalCritial}
                  onChange={e => { setSaveOption(true); setParameterColor({ ...parameterColor, finalCritial: e.target.value }); }}
                />
                <ColorRow
                  label="Borderline"
                  value={parameterColor.oneFromNormal}
                  onChange={e => { setSaveOption(true); setParameterColor({ ...parameterColor, oneFromNormal: e.target.value }); }}
                />
                <ColorRow
                  label="Very High"
                  value={parameterColor.twoFromNormal}
                  onChange={e => { setSaveOption(true); setParameterColor({ ...parameterColor, twoFromNormal: e.target.value }); }}
                />
                <ColorRow
                  label="Very Very High"
                  value={parameterColor.threeFromNormal}
                  onChange={e => { setSaveOption(true); setParameterColor({ ...parameterColor, threeFromNormal: e.target.value }); }}
                />
              </div>
            </div>

            {/* 4. Combined Profile & Header - Wide Card */}
            <div className='cc-card cc-card-wide'>
              <div className='cc-card-header'>
                <div className='cc-icon-box' style={{ background: '#fff7ed', color: '#f97316' }}><FaFileMedicalAlt /></div>
                <h3 className='cc-card-title'>Header & Profile Styling</h3>
              </div>
              <p className='cc-card-desc'>
                Patient details labels and values; profile section heading and background (e.g. Lipid Profile).
              </p>
              <div className='cc-card-body'>
                <div className="cc-grid-2-col">
                  <div>
                    <h5 style={{ fontSize: '0.85rem', color: '#94a3b8', marginBottom: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>Patient Header</h5>
                    <ColorRow
                      label="Heading Color"
                      value={patientHeaderHeading}
                      onChange={e => { setSaveOption(true); setPatientHeaderHeading(e.target.value); }}
                    />
                    <div style={{ height: '0.5rem' }}></div>
                    <ColorRow
                      label="Value Color"
                      value={patientHeaderValue}
                      onChange={e => { setSaveOption(true); setPatientHeaderValue(e.target.value); }}
                    />
                  </div>
                  <div>
                    <h5 style={{ fontSize: '0.85rem', color: '#94a3b8', marginBottom: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>Report Profile</h5>
                    <ColorRow
                      label="Heading Color"
                      value={profileHeading}
                      onChange={e => { setSaveOption(true); setProfileHeading(e.target.value); }}
                    />
                    <div style={{ height: '0.5rem' }}></div>
                    <ColorRow
                      label="Background Color"
                      value={profileBackground}
                      onChange={e => { setSaveOption(true); setProfileBackground(e.target.value); }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Empty Standard Card if needed for layout balance or future expansion */}
            {/* <div className='cc-card cc-card-standard'>...</div> */}

          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default ColorConfig;
